import { immerable } from "immer";
import { AbstractTimeSlot } from "@/pages/human-resources/time-management/business/time-slot/AbstractTimeSlot.ts";
import { DateTime } from "luxon";
import type { HourlyTagCategory } from "@/pages/human-resources/time-management/business/hourly-tag/HourlyTagCategory.ts";

export class TimeSlot extends AbstractTimeSlot {
  [immerable] = true;
  constructor(
    category: HourlyTagCategory | undefined,
    positionId: string | undefined,
    breakDurationMinutes: number,
    public readonly id: string | undefined,
    public readonly startTime: Date,
    public readonly endTime: Date,
  ) {
    super(category, positionId, breakDurationMinutes);
  }

  public sameValueAs(other: TimeSlot | undefined): boolean {
    return (
      other !== undefined &&
      super.sameValueAs(other) &&
      this.startTime.getTime() == other.startTime.getTime() &&
      this.endTime.getTime() == other.endTime.getTime()
    );
  }

  public buildKey(): string {
    return (
      this.id ??
      `${this.startTime.toISOString()}_to_${this.endTime.toISOString()}`
    );
  }

  public covers(dt: DateTime): boolean {
    const dtMillis = dt.toMillis();
    return (
      this.startTime.getTime() <= dtMillis && dtMillis <= this.endTime.getTime()
    );
  }

  public overlapsAnyOf(slots: TimeSlot[]): boolean {
    return slots.some((s) => s != this && this.overlaps(s));
  }

  public overlaps(other: TimeSlot): boolean {
    const start = this.startTime.getTime();
    const end = this.endTime.getTime();
    const otherStart = other.startTime.getTime();
    const otherEnd = other.endTime.getTime();
    return Math.max(start, otherStart) < Math.min(end, otherEnd);
  }

  protected durationMinutes(): number {
    const start = DateTime.fromJSDate(this.startTime);
    const end = DateTime.fromJSDate(this.endTime);
    return end.diff(start, "minutes").minutes;
  }
}
